import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, catchError, concat, filter, finalize, map, mergeMap, Observable, of, Subject, take, takeUntil, tap, throwError } from 'rxjs';
import { PaginatedList } from 'src/app/_avmin/models/paginated-list';
import { SignalRService } from 'src/app/_avmin/services/signalr.service';
import { SweetAlertService } from 'src/app/_avmin/services/sweet-alert.service';
import { LayoutService } from 'src/app/_layout/layout.module';
import { environment } from 'src/environments/environment';
import { CashMovementType, ISaleRegisterShiftCashMovement } from '../models/sale-register-shift-cash-movement';
import { ISaleRegisterShiftCashMovements } from '../models/sale-register-shift-cash-movements';
import { ISaleRegisterShiftDetails } from '../models/sale-register-shift-details.model';
import { ISaleRegisterShiftPayment } from '../models/sale-register-shift-payment.model';
import { ISaleRegisterShift } from '../models/sale-register-shift.model';
import { ISalesRegister } from '../models/sales-register.model';
import { ISale } from '../../sales/models/sale.model';

const API_REGISTER_URL = `${environment.apiUrl}/sales_registers`;
const LS_CURRENT_REGISTER_ID = 'avmin.register:current.id';
const LS_CURRENT_SHIFT_ID = 'avmin.register:current.shift_id';
const LS_LAST_REGISTER_CLOSED_ID = 'avmin.register:last.closed.id';
const LS_LAST_REGISTER_OPENED_ID = 'avmin.register:last.opened.id';

interface IRegisterOpenResponse {
  register: ISalesRegister;
  shift: ISaleRegisterShiftDetails;
}

interface IRegisterCloseResponse {
  register: ISalesRegister;
  shift: ISaleRegisterShift;
}

export interface ISalesRegisterRequest {
  name: string;
}

export interface ISalesRegisterShiftFilter {
  dateFrom?: Date;
  dateTo?: Date;
  orderBy: string;
  pageNo: number;
  pageSize: number;
  registerId?: string;
}

@Injectable({ providedIn: 'root'})
export class SalesRegisterService implements OnDestroy {

  private readonly _http = inject(HttpClient);
  private readonly _layout = inject(LayoutService);
  private readonly _logger = inject(NGXLogger);
  private readonly _signalR = inject(SignalRService);
  private readonly _router = inject(Router);
  private readonly _swal = inject(SweetAlertService);
  private readonly _destroyed$ = new Subject<void>();
  private readonly _register$ = new BehaviorSubject<ISalesRegister | undefined>(undefined);
  private readonly _registers$ = new BehaviorSubject<ISalesRegister[]>([]);
  private readonly _registerClosed$ = new Subject<void>();
  private readonly _shift$ = new BehaviorSubject<ISaleRegisterShiftDetails | undefined>(undefined);

  public readonly register$ = this._register$.asObservable();
  public readonly registers$ = this._registers$.asObservable();
  public readonly registerClosed$ = this._registerClosed$.asObservable();
  public readonly shift$ = this._shift$.asObservable();

  public get register(): ISalesRegister | undefined {
    return this._register$.getValue();
  }

  constructor() {
    this._bindSignalR();
  }

  private _bindSignalR() {
    this._signalR.salesRegister$
      .pipe(takeUntil(this._destroyed$))
      .subscribe(data => {
        switch(data.action) {
          case 'closed':
            return this._handleSignalRRegisterClosed(data.id as string);

          case 'opened':
            return this._handleSignalRRegisterOpened(data.id as string);
        }
      });
  }

  private _clearShift() {
    this._shift$.next(undefined);
    localStorage.removeItem(LS_CURRENT_SHIFT_ID);
  }

  private _handleSignalRRegisterClosed(registerId: string) {
    this._logger.debug('[SalesRegisterService].[handleSignalRRegisterClosed] Register closed via SignalR', registerId);

    // Was it us that just closed it?
    if (localStorage.getItem(LS_LAST_REGISTER_CLOSED_ID) === registerId.toString()) {
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterClosed] we just closed it');
      localStorage.removeItem(LS_LAST_REGISTER_CLOSED_ID);
      return;
    }

    const currentRegister = this._register$.getValue();
    if (currentRegister?.id !== registerId) {
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterClosed] not our register');
      // Not our register, don't care
      return;
    }

    // It's this register, and we didn't close it
    this._getLocalRegister().subscribe(_ => {
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterClosed] register closed, reloaded');
      localStorage.removeItem(LS_LAST_REGISTER_CLOSED_ID);
      this._registerClosed$.next();
      this._swal.danger('Register Closed', 'This register has just been closed for the day on another device.');
    });
  }

  private _handleSignalRRegisterOpened(registerId: string) {
    this._logger.debug('[SalesRegisterService].[handleSignalRRegisterOpened] Register opened via SignalR', registerId);

    // Was it just us that opened it?
    if (localStorage.getItem(LS_LAST_REGISTER_OPENED_ID) === registerId.toString()) {
      // lol yes
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterOpened] we just opened it');
      localStorage.removeItem(LS_LAST_REGISTER_OPENED_ID);
      return;
    }

    const currentRegister = this._register$.getValue();
    if (currentRegister?.id != registerId) {
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterOpened] not our register');
      // Not our register, don't care
      return;
    }

    // It's this register, and we didn't open it
    this._getLocalRegister().subscribe(_ => {
      this._logger.debug('[SalesRegisterService].[handleSignalRRegisterOpened] register opened, reloaded');
      localStorage.removeItem(LS_LAST_REGISTER_OPENED_ID);
      this._swal.info('Register Opened', 'This register has just been opened for the day on another device. You can now start selling.');
    });
  }

  private _getLocalRegister(): Observable<ISalesRegister | undefined> {
    const idString = localStorage.getItem(LS_CURRENT_REGISTER_ID);
    if (!idString) { return of(undefined); }

    return this._http.get<ISalesRegister>(`${API_REGISTER_URL}/${idString}`)
      .pipe(
        filter(register => !!register),
        map(register => this._mapRegister(register!)),
        tap(register => this._register$.next(register)),
        mergeMap(register => this.getCurrentShift(register).pipe(
          tap(shift => this.switchToRegister(register, shift!)),
          map(_ => register))
        ),
      );
  }

  private _mapRegister(register: ISalesRegister): ISalesRegister {
    register.openedAtUtc = new Date(register.openedAtUtc);
    return register;
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  addCashMovement(amount: number, note: string, type: CashMovementType): Observable<ISaleRegisterShiftCashMovement> {
    const shift = this._shift$.getValue();
    if (!shift) {
      return throwError(() => 'No shift is currently open');
    }

    return this._http.post<ISaleRegisterShiftCashMovement>(`${API_REGISTER_URL}/cash`, {
      shiftId: shift.id,
      amount,
      note,
      type
    });
  }

  canClose(register: ISalesRegister): Observable<void> {
    return this._http.get<void>(`${API_REGISTER_URL}/${register.id}/close`);
  }

  close(register: ISalesRegister, note: string, payments: ISaleRegisterShiftPayment[], cashBanked: number, closingFloat: number): Observable<IRegisterCloseResponse> {
    localStorage.setItem(LS_LAST_REGISTER_CLOSED_ID, register.id.toString());

    return this._http.post<IRegisterCloseResponse>(`${API_REGISTER_URL}/close`, {
        registerId: register.id,
        note,
        payments,
        cashBanked,
        closingFloat
      })
      .pipe(
        tap(response => {
          this._logger.debug('[SalesRegisterService].[close] Register closed', response.register.id);
          this._register$.next(this._mapRegister(response.register));
          this._clearShift();
        }),
        catchError(e => {
          localStorage.removeItem(LS_LAST_REGISTER_CLOSED_ID);
          return throwError(() => e);
        })
      );
  }

  closeStart() {
    const register = this._register$.getValue();
    if (!register || !register.isOpen) { return; }

    this._layout.showPageLoading();

    return this.canClose(register!)
      .pipe(
        finalize(() => this._layout.hidePageLoading())
      )
      .subscribe({
        next: () => this._router.navigateByUrl('/pos/register/close'),
        error: e => this._swal.handleError(e)
      });
  }

  create(register: ISalesRegisterRequest): Observable<ISalesRegister> {
    return this._http.post<ISalesRegister>(API_REGISTER_URL, register)
  }

  delete(registerId: string): Observable<void> {
    return this._http.delete<void>(`${API_REGISTER_URL}/${registerId}`);
  }

  disablePaymentType(registerId: string, paymentTypeId: number): Observable<void> {
    return this._http.delete<void>(`${API_REGISTER_URL}/${registerId}/payment_type/${paymentTypeId}`, {});
  }

  enablePaymentType(registerId: string, paymentTypeId: number): Observable<void> {
    return this._http.post<void>(`${API_REGISTER_URL}/${registerId}/payment_type/${paymentTypeId}`, {});
  }

  findShift(id: number): Observable<ISaleRegisterShiftDetails> {
    return this._http.get<ISaleRegisterShiftDetails>(`${API_REGISTER_URL}/shift/${id}` );
  }

  get(): Observable<ISalesRegister[]> {
    return this._http.get<ISalesRegister[]>(API_REGISTER_URL)
      .pipe(tap(registers => {
        registers = registers.map(r => this._mapRegister(r));
        this._registers$.next(registers);
      }));
  }

  getCurrent(): Observable<ISalesRegister | undefined> {
    return concat(
      this._register$.pipe(take(1), filter(r => !!r)),
      this._getLocalRegister().pipe(filter(r => !!r)),
      this._register$.asObservable().pipe(take(1))
    );
  }

  getCurrentShift(register: ISalesRegister): Observable<ISaleRegisterShiftDetails | undefined> {
    return this._http.get<ISaleRegisterShiftDetails>(`${API_REGISTER_URL}/${register.id}/shift/current`)
      .pipe(catchError(e => {
        if (e.status === 404) { return of(undefined); }
        return throwError(() => e);
      }));
  }

  getPayments(register: ISalesRegister): Observable<ISaleRegisterShiftPayment[]> {
    return this._http.get<ISaleRegisterShiftPayment[]>(`${API_REGISTER_URL}/${register.id}/payments`);
  }

  getShifts(filter: Partial<ISalesRegisterShiftFilter>): Observable<PaginatedList<ISaleRegisterShift>> {
    let q: any = {
      orderBy: filter.orderBy,
      pageNo: filter.pageNo,
      pageSize: filter.pageSize
    }

    if (filter.dateFrom !== undefined) { q.dateFrom = filter.dateFrom.toISOString(); }
    if (filter.dateTo !== undefined) { q.dateTo = filter.dateTo.toISOString(); }
    if (filter.registerId !== undefined) { q.registerId = filter.registerId; }

    return this._http.get<PaginatedList<ISaleRegisterShift>>(`${API_REGISTER_URL}/shifts`, { params: q });
  }

  getShiftCashMovements(shiftId: number): Observable<ISaleRegisterShiftCashMovements> {
    return this._http.get<ISaleRegisterShiftCashMovements>(`${API_REGISTER_URL}/shift/${shiftId}/cash`);
  }

  getShiftPayments(shiftId: number): Observable<ISaleRegisterShiftPayment[]> {
    return this._http.get<ISaleRegisterShiftPayment[]>(`${API_REGISTER_URL}/shift/${shiftId}/payments`);
  }

  getShiftSales(shiftId: number): Observable<ISale[]> {
    return this._http.get<ISale[]>(`${API_REGISTER_URL}/shift/${shiftId}/sales`);
  }

  find(registerId: string): Observable<ISalesRegister> {
    return this._http.get<ISalesRegister>(`${API_REGISTER_URL}/${registerId}`);
  }

  open(register: ISalesRegister, openingFloat: number, note: string): Observable<IRegisterOpenResponse> {
    localStorage.setItem(LS_LAST_REGISTER_OPENED_ID, register.id.toString());

    return this._http.post<IRegisterOpenResponse>(`${API_REGISTER_URL}/${register.id}/open`, { openingFloat, note })
      .pipe(
        map(response => {
          response.register = this._mapRegister(response.register);
          return response;
        }),
        tap(response => {
          this._logger.debug('[SalesRegisterService].[open] Register opened', response.register.id);
          this.switchToRegister(response.register, response.shift);
        }),
        catchError(e => {
          localStorage.removeItem(LS_LAST_REGISTER_OPENED_ID);
          return throwError(() => e);
        })
      );
  }

  switchToRegister(register: ISalesRegister, shift: ISaleRegisterShiftDetails | undefined) {
    this._logger.debug('[RegisterService].[switchToRegister]', register, shift)
    this._register$.next(register);
    this._shift$.next(shift);

    localStorage.setItem(LS_CURRENT_REGISTER_ID, register.id.toString());

    if (!!shift) {
      localStorage.setItem(LS_CURRENT_SHIFT_ID, shift?.id.toString());
    } else {
      localStorage.removeItem(LS_CURRENT_SHIFT_ID);
    }
  }

  update(registerId: string, register: ISalesRegisterRequest): Observable<ISalesRegister> {
    return this._http.put<ISalesRegister>(`${API_REGISTER_URL}/${registerId}`, register);
  }
}
